import { onMounted, onUnmounted, ref } from 'vue'
import axios from 'axios'
import { resolveApiBaseUrl } from '@/services/api'

const POLL_MS = 5 * 60 * 1000

/**
 * Compare SPA build id (VITE_APP_RELEASE_ID) to API /app-meta release_id.
 * When both are set and differ, prompt for reload after deploy.
 */
export function useAppReleaseWatcher() {
  const showUpdateBanner = ref(false)
  const serverReleaseId = ref<string | null>(null)

  const clientReleaseId = ((import.meta.env.VITE_APP_RELEASE_ID as string) || '').trim()

  async function fetchServerRelease(): Promise<string | null> {
    try {
      const base = resolveApiBaseUrl()
      const { data } = await axios.get<{ release_id?: string }>(`${base}/app-meta`, {
        timeout: 15000,
        headers: { Accept: 'application/json' },
      })
      const id = typeof data?.release_id === 'string' ? data.release_id.trim() : ''
      return id || null
    } catch {
      return null
    }
  }

  async function checkMismatch() {
    if (!clientReleaseId) {
      showUpdateBanner.value = false
      return
    }
    const serverId = await fetchServerRelease()
    serverReleaseId.value = serverId
    if (!serverId) {
      return
    }
    if (serverId !== clientReleaseId) {
      showUpdateBanner.value = true
    }
  }

  function reloadApp() {
    window.location.reload()
  }

  let intervalId: ReturnType<typeof setInterval> | null = null

  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      void checkMismatch()
    }
  }

  onMounted(() => {
    void checkMismatch()
    intervalId = setInterval(() => void checkMismatch(), POLL_MS)
    document.addEventListener('visibilitychange', onVisibility)
  })

  onUnmounted(() => {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
    document.removeEventListener('visibilitychange', onVisibility)
  })

  return {
    showUpdateBanner,
    clientReleaseId,
    serverReleaseId,
    reloadApp,
    checkMismatch,
  }
}
